import axios from "../../../api/axios";

// Get API base URL for serving images
const getApiBaseUrl = () => {
  if (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
    return 'https://baptistmenapi.onrender.com';
  }
  return 'http://localhost:3001';
};

/**
 * Fetches all required badge data and opens a print-ready badge in a new window.
 * @param {number|string} userId - The UserID
 * @param {string} token - Auth token
 */
export async function printUserBadge(userId, token) {
  // Use the passed token, falling back to localStorage
  const currentToken = token || localStorage.getItem("token");
  
  const requestConfig = {
    withCredentials: true,
    headers: currentToken ? { Authorization: `Bearer ${currentToken}` } : {}
  };

  try {
    // Fetch user data
    const userResponse = await axios.get(`/api/users/${userId}`, requestConfig);
    const user = userResponse.data;

    // Fetch user settings
    let userSettings = {};
    try {
      const settingsResponse = await axios.get(`/api/users/usersettings/${userId}`, requestConfig);
      userSettings = settingsResponse.data || {};
      //console.log('User Settings for badge:', userSettings);
      //console.log('BP_BadgeFlagline:', userSettings.BP_BadgeFlagline);
    } catch (e) {
      console.error('Failed to fetch user settings for badge:', e);
    }

    // Fetch certifications by UserID
    let certifications = [];
    try {
      const certResponse = await axios.get(`/api/certification/by-userid/${userId}`, requestConfig);
      certifications = certResponse.data || [];
    } catch (e) {
      console.error('Failed to fetch certifications for badge:', e);
    }

    // Convert logo to a data URL so it works in the blank print window
    const logoAbsoluteUrl = `${window.location.origin}/Images/Check-in/id-badge-logo-nc-2024.png`;
    let logoDataUrl = '';
    try {
      logoDataUrl = await imageToDataUrl(logoAbsoluteUrl);
    } catch (e) {
      console.error('Failed to load logo image:', e);
    }

    // Convert profile photo to data URL if available
    const profileImageUrl = buildImageUrl(user, userSettings);
    let photoDataUrl = '';
    if (profileImageUrl) {
      try {
        photoDataUrl = await imageToDataUrl(profileImageUrl);
      } catch (e) {
        console.error('Failed to load profile image:', e);
      }
    }

    // Build the badge data
    // Construct full name with middle name if available
    const nameParts = [
      user.FirstName || '',
      user.MiddleName || '',
      user.LastName || ''
    ].filter(part => part && part.trim()).join(' ');
    
    // Extract role display name
    let roleName = '';
    if (user.CMS_UserRole && user.CMS_UserRole.length > 0) {
      const primaryRole = user.CMS_UserRole[0];
      roleName = primaryRole.CMS_Role?.RoleDisplayName || primaryRole.CMS_Role?.RoleName || '';
    }
    
    // Preferred Name (UserNickName). Ignore values that look like file paths
    // (legacy/dirty data). When empty, fall back to the full name per spec.
    let preferredName = userSettings.UserNickName || '';
    if (preferredName.includes('/') || preferredName.includes('\\')) {
      preferredName = '';
    }
    if (!preferredName) {
      preferredName = nameParts || '';
    }

    const badgeData = {
      userId: user.UserID,
      firstName: user.FirstName || '',
      lastName: user.LastName || '',
      preferredName: preferredName,
      fullName: nameParts || 'Unknown User',
      flagLine: userSettings.BP_BadgeFlagline || '',
      roleName: roleName,
      certifications: certifications,
      profileImageUrl: photoDataUrl || profileImageUrl,
      // Badge shows the background check EXPIRATION (check date + 3 years)
      bgCheckDate: formatBgCheckExpiration(userSettings.BP_BGCheckDate) || 'N/A',
      logoDataUrl: logoDataUrl,
    };

    openBadgePrintWindow(badgeData);
  } catch (error) {
    console.error('Failed to load badge data:', error);
    alert('Failed to load badge data. Please try again.');
  }
}

/**
 * Converts an image URL to a base64 data URL.
 * Uses a canvas to draw the image, then exports as PNG data URL.
 */
function imageToDataUrl(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      try {
        resolve(canvas.toDataURL('image/png'));
      } catch (e) {
        reject(e);
      }
    };
    img.onerror = () => reject(new Error('Failed to load image: ' + url));
    img.src = url;
  });
}

function buildImageUrl(user, userSettings) {
  // Priority: userSettings.UserPicture > user.ProfileImage > legacy MiddleName
  const raw = userSettings.UserPicture || user.ProfileImage || user.userSettings?.UserPicture || '';
  if (!raw) {
    // Check legacy path
    if (user.MiddleName && typeof user.MiddleName === 'string' && user.MiddleName.startsWith('/uploads/')) {
      return `${getApiBaseUrl()}${user.MiddleName}`;
    }
    return '';
  }
  if (/^https?:\/\//i.test(raw)) return raw;
  return `${getApiBaseUrl()}${raw}`;
}

function formatDate(dateVal) {
  if (!dateVal) return '';
  const d = new Date(dateVal);
  if (isNaN(d.getTime())) return '';
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const year = d.getFullYear();
  return `${month}/${day}/${year}`;
}

/**
 * Background checks are valid for 3 years. Takes the date the check was done
 * and returns the expiration date (MM/DD/YYYY) = check date + 3 years.
 *
 * If the value starts with YYYY-MM-DD, those parts are used directly so the
 * date can't shift a day because of timezone conversion. A Feb 29 check date
 * lands on Feb 28 when the target year is not a leap year.
 */
function formatBgCheckExpiration(dateVal, yearsValid = 3) {
  if (!dateVal) return '';

  let year, month, day;

  if (typeof dateVal === 'string') {
    const m = dateVal.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) {
      year = parseInt(m[1], 10);
      month = parseInt(m[2], 10);
      day = parseInt(m[3], 10);
    }
  }

  if (!Number.isFinite(year)) {
    const d = new Date(dateVal);
    if (isNaN(d.getTime())) return '';
    year = d.getFullYear();
    month = d.getMonth() + 1;
    day = d.getDate();
  }

  const expYear = year + yearsValid;

  // Clamp the day so Feb 29 doesn't roll over into March
  const daysInExpMonth = new Date(expYear, month, 0).getDate();
  const expDay = Math.min(day, daysInExpMonth);

  return `${String(month).padStart(2, '0')}/${String(expDay).padStart(2, '0')}/${expYear}`;
}

function formatCertDate(dateVal) {
  if (!dateVal) return '';
  // Business rule: every certification expires on December 31 of its year.
  // Pull the year from whatever the API returned and force 12/31 — this also
  // shields the badge from any legacy 12/30 rows still sitting in the DB.
  let year;
  if (typeof dateVal === 'string') {
    const m = dateVal.match(/^(\d{4})/);
    if (m) year = parseInt(m[1], 10);
  }
  if (!Number.isFinite(year)) {
    const d = new Date(dateVal);
    if (isNaN(d.getTime())) return '';
    year = d.getUTCFullYear();
  }
  return Number.isFinite(year) ? `12/31/${year}` : '';
}

function openBadgePrintWindow(data) {
  const printWindow = window.open('', '_blank', 'width=600,height=900');
  if (!printWindow) {
    alert('Please allow popups to print the badge.');
    return;
  }

  // Build certifications rows (replaces <bp:Memberships>)
  // Expired certifications should not display on the printed badge.
  const certRows = (data.certifications || [])
    .filter(c => String(c.Status || '').toLowerCase() !== 'expired')
    .map(c => {
      const dateStr = formatCertDate(c.Expiration) || 'No Expiry';
      const certName = (c.Certification || '').trim();
      return `<tr><td class="cert-date">${dateStr}</td><td class="cert-name">${certName}</td></tr>`;
    }).join('');

  // Build photo HTML (replaces GetUserAvatarImage)
  let photoHtml;
  if (data.profileImageUrl) {
    photoHtml = `<img src="${data.profileImageUrl}" alt="Photo" />`;
  } else {
    // Try to create initials from first and last name
    const firstInitial = (data.firstName && data.firstName.length > 0) ? data.firstName.charAt(0).toUpperCase() : '';
    const lastInitial = (data.lastName && data.lastName.length > 0) ? data.lastName.charAt(0).toUpperCase() : '';
    const initials = firstInitial + lastInitial;
    
    if (initials) {
      photoHtml = `<div class="photo-placeholder" style="width:120px;height:120px;background-color:#073567;color:#fff;display:flex;align-items:center;justify-content:center;font-size:40px;font-weight:bold;">${initials}</div>`;
    } else {
      // No name available - show generic user icon placeholder
      photoHtml = `<div class="photo-placeholder" style="width:120px;height:120px;background-color:#f0f0f0;color:#666;display:flex;align-items:center;justify-content:center;font-size:60px;font-weight:normal;">👤</div>`;
    }
  }

  // Build logo HTML with data URL
  const logoSrc = data.logoDataUrl || '';

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8"/>
<title>Badge - ${escapeHtml(data.fullName)}</title>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body {
    font-family: Arial, Helvetica, sans-serif;
    background: #f5f5f5;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 20px 0;
  }
  #qrcodeTable {
    text-align: center;
    display: inline-block
  }
  .photo img {
    width: 120px;
    height: 120px;
    object-fit: cover;
    display: block;
    margin: 0;
    padding: 0
  }
  .photo-placeholder {
    width: 120px;
    height: 120px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    margin: 0;
    padding: 0
  }
  .wrapper {
    display: block;
    width: 252px;
    margin: 0 auto;
    padding: 0;
  }
  .qrcode {
    margin-top: 10px
  }
  .badge {
    width: 252px;
    height: 400px;
    overflow: hidden;
    position: relative;
    background: #fff;
    border: 1px solid #ddd;
  }
  section {
    display: block;
    width: 100%;
    float: left;
    margin: 0;
    padding: 0
  }
  .blue-border {
    display: block;
    width: 100%;
    height: 20px;
    float: left;
    background-color: #073567;
    margin: 0;
    padding: 0;
    line-height: 0
  }
  .top {
    margin: 0;
    padding: 0;
    margin-bottom: 5px;
    clear: both
  }
  .photo {
    width: 50%;
    float: left;
    margin: 0;
    padding: 0;
    line-height: 0;
    vertical-align: top
  }
  .info {
    width: 50%;
    float: right;
    text-align: center;
    color: #073567
  }
  .preferred {
    text-align: center;
    font-size: 28px;
    font-weight: bold;
    color: #073567;
    margin-top: 10px;
    margin-bottom: 5px
  }
  .full-name-display {
    text-align: center;
    font-size: 16px;
    color: #073567;
    margin-bottom: 10px;
    font-style: italic
  }
  .logo {
    margin-top: 5px;
    text-align: center
  }
  .logo img {
    width: 135px;
    height: auto
  }
  .contact {
    font-size: 12px;
    line-height: 14px;
    text-align: center;
    margin-top: 5px;
    margin-bottom: 10px;
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0
  }
  .first-name {
    margin-top: 4px
  }
  .first-name, .last-name {
    font-size: 18px;
    line-height: 18px;
    margin-bottom: 5px;
    font-weight: bold
  }
  .date {
    margin-bottom: 5px
  }
  .role-name {
    background-color: #FCD245;
    width: 100%;
    padding-top: 5px;
    padding-bottom: 5px;
    font-weight: bold;
    font-size: 14px;
    text-align: center;
    color: #073567
  }
  .flag {
    background-color: #FCD245;
    width: 100%;
    padding-top: 5px;
    padding-bottom: 5px;
    font-weight: bold;
    font-size: 14px;
    text-align: center
  }
  .certifications {
    font-size: 13px;
    padding-left: 8px;
    padding-right: 8px;
    padding-top: 8px;
    padding-bottom: 80px;
    min-height: 220px;
    max-height: 300px;
    overflow-y: auto
  }
  .certifications table {
    width: 100%;
    border-collapse: collapse
  }
  .certifications td {
    padding: 3px 2px;
    vertical-align: top;
    font-size: 12px;
    line-height: 1.3
  }
  .cert-date {
    white-space: nowrap;
    width: 85px;
    font-weight: bold;
    text-align: right;
    padding-right: 8px
  }
  .cert-name {
    padding-left: 4px
  }
  .page-break {
    display: block;
    width: 252px;
    height: 1px;
    background: #ddd;
    margin: 10px auto;
    border: none;
  }

  /* PRINT STYLES */
  @media print {
    * {
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
      color-adjust: exact;
    }
    body {
      background: #fff;
      padding: 0 !important;
      margin: 0 !important;
      display: block;
    }
    .wrapper {
      margin: 0 !important;
      padding: 0 !important;
      width: 252px;
    }
    .badge {
      border: none;
      margin: 0;
      padding: 0;
    }
    .blue-border {
      margin: 0 !important;
      padding: 0 !important;
      background-color: #073567 !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
      color-adjust: exact;
    }
    .top {
      margin: 0 !important;
      padding: 0 !important;
    }
    .photo {
      margin: 0 !important;
      padding: 0 !important;
    }
    .photo img {
      margin: 0 !important;
      padding: 0 !important;
      display: block;
    }
    .info {
      color: #073567 !important;
    }
    .preferred {
      color: #073567 !important;
    }
    .full-name-display {
      color: #073567 !important;
    }
    .role-name {
      background-color: #FCD245 !important;
      color: #073567 !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
      color-adjust: exact;
    }
    .flag {
      background-color: #FCD245 !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
      color-adjust: exact;
    }
    .certifications {
      overflow: visible !important;
      max-height: none !important;
    }
    .page-break {
      display: block;
      height: 0;
      background: none;
      margin: 0;
      page-break-after: always;
    }
    @page {
      size: 252px 400px;
      margin: 0;
    }
  }
</style>
</head>
<body>

<div class="wrapper">
<div class="badge">
<div class="blue-border"></div>
<section class="top">
<div class="photo">${photoHtml}</div>
<div class="info">
<div class="first-name">${escapeHtml(data.firstName || 'Unknown')} ${escapeHtml(data.lastName || 'User')}</div>
<div class="date">${escapeHtml(data.bgCheckDate || 'N/A')}</div>
${data.flagLine ? `<div class="flag">${escapeHtml(data.flagLine)}</div>` : ''}
</div>
</section>
<section class="preferred">${escapeHtml(data.preferredName || data.fullName || 'Unknown User')}</section>
<section class="logo">${logoSrc ? `<img src="${logoSrc}" width="450" height="426" alt=""/>` : '<div style="color:#999;font-size:11px;">Logo unavailable</div>'}</section>
<section class="contact">To Verify Call 800.395.5102 x5600</section>
</div>
</div>
<div class="page-break"></div>
<div class="wrapper">
<div class="badge">
<div class="blue-border"></div>
<section class="certifications">
<table>${certRows || '<tr><td colspan="2" style="text-align:center;color:#999;">No certifications</td></tr>'}</table>
</section>
<section class="contact">
If found, please return to:<br/>
<strong>Baptists on Mission<br/>
205 Convention Drive<br/>Cary, North Carolina 27512</strong>
</section>
</div>
</div>

<script>
  // Auto-print after a brief delay to ensure rendering is complete
  setTimeout(function() {
    window.print();
  }, 300);
</script>

</body>
</html>`;

  printWindow.document.write(html);
  printWindow.document.close();
}

function escapeHtml(str) {
  if (!str) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}